/* deviceFilterColumns.js */

/**
 * @module deviceFilterColumns
 *
 * Single source of truth for the device columns offered by a rule's
 * `ifExpression` advanced filter. Previously this object literal lived inline
 * in `EditAuditPolicyComponent.columnConfig`; it's extracted here so the
 * policy-level JSON editor (which has no per-rule component) can derive the
 * exact same valid-field set for validation instead of duplicating the key list.
 *
 * Node props are layered on separately by the caller as `nodeProps.<id>`.
 */
const _ = require("lodash");
const {
    DEVICE_MANAGEMENT_STATES,
    LICENSE_STATUS,
    DEVICE_ENVIRONMENTS,
} = require("../../../lmrbcomponents/deviceexplorer/views/device_grid_view/deviceConstants");

const _osPlanOptions = (values) => _.mapValues(values || {}, (title, key) => ({name: key, title}));

/**
 * Build the base device-filter column map.
 *
 * @param {Object} deps
 * @param {Object} deps.vendorsMap            {key: title}
 * @param {Object} deps.accessStatusLabels    {key: title}
 * @param {Object} deps.typeBaseGroupMap      {key: name}
 * @param {Array}  deps.osImageServerList     [{name}]
 * @param {Object} deps.osMap                 {key: title}
 * @param {Array}  deps.sitePaths             [{id, path}]
 * @param {Object} deps.locationTypeLabels    {key: title}
 * @param {Object} deps.osUpgradeStatusValues {key: title}
 * @param {Array}  deps.tags                  [{id, name}]
 * @returns {Object} column map keyed by field name
 */
const buildDeviceFilterColumns = ({
    vendorsMap = {},
    accessStatusLabels = {},
    typeBaseGroupMap = {},
    osImageServerList = [],
    osMap = {},
    sitePaths = [],
    locationTypeLabels = {},
    osUpgradeStatusValues = {},
    tags = [],
} = {}) => {
    const optionFilterList = {};
    _.sortBy(Object.keys(typeBaseGroupMap)).forEach((key) => {
        optionFilterList[key] = {name: typeBaseGroupMap[key], title: typeBaseGroupMap[key]};
    });

    const serversMap = (osImageServerList || []).map(({name}) => ({title: name, name}));

    const optionMapAccessStatus = _.mapValues(accessStatusLabels, (title, key) => ({
        name: key,
        title,
    }));

    const optionMapSitePaths = sitePaths.map(({id, path}) => ({name: id, title: path}));

    const optionMapTags = (tags || []).map(({id, name}) => ({name: id, title: name}));

    return {
        detectedTypeBase: {
            label: "Type",
            entityColumn: true,
            options: optionFilterList,
            dataType: "enum",
        },
        name: {label: "Name", dataType: "string"},
        enable: {label: "Enable", dataType: "boolean"},
        description: {label: "Description", dataType: "string"},
        managementState: {
            label: "Management State",
            options: DEVICE_MANAGEMENT_STATES,
            dataType: "enum",
        },
        environment: {label: "Environment", options: DEVICE_ENVIRONMENTS, dataType: "enum"},
        licensed: {label: "Licensed", options: LICENSE_STATUS, dataType: "boolean"},
        "connectionInformation.ip": {
            label: "IP Address",
            path: "connectionInformation.ip",
            dataType: "string",
        },
        detectedVendor: {
            label: "Vendor",
            dataType: "enum",
            options: _.map(vendorsMap, (title) => ({name: title, title})),
        },
        detectedHostName: {label: "Hostname", dataType: "string"},
        detectedOs: {
            label: "OS",
            dataType: "enum",
            options: _.map(osMap, (title) => ({name: title, title})),
        },
        detectedSoftwareVersion: {label: "OS Version", dataType: "string"},
        detectedSerialNumber: {label: "Serial", dataType: "string"},
        detectedSku: {label: "SKUs", dataType: "string"},
        detectedUptime: {label: "Up Time", dataType: "string"},
        elementCount: {label: "Elements", dataType: "number"},
        fileServerName: {label: "File Server", options: serversMap, dataType: "enum"},
        haPairName: {label: "HA Group", dataType: "string"},
        osUpgradedBy: {label: "OSM Action By", dataType: "string"},
        osUpgradedOn: {label: "OSM Action On", dataType: "string"},
        osUpgradeError: {label: "OSM Action Error", dataType: "string"},
        osUpgradePlanAction: {label: "OS Plan Action", dataType: "string"},
        osUpgradePlanId: {label: "OS Plan ID ", dataType: "string"},
        osUpgradePlanName: {label: "OS Plan Name", dataType: "string"},
        osUpgradeStatus: {
            label: "OSM Action Status",
            dataType: "enum",
            options: _osPlanOptions(osUpgradeStatusValues),
        },
        sitePath: {label: "Site Path", dataType: "string"},
        siteId: {label: "Site Path List", dataType: "enum", options: optionMapSitePaths},
        siteName: {label: "Site Name", dataType: "string"},
        siteCodeName: {label: "Site Code Name", dataType: "string"},
        siteLocationType: {
            label: "Site Type",
            dataType: "enum",
            options: _.map(locationTypeLabels, (title) => ({name: title, title})),
            context: {_labels: locationTypeLabels},
        },
        autoAssignEnabled: {label: "Site Auto Assign", dataType: "boolean"},
        useIssu: {
            label: "Non-disruptive",
            dataType: "boolean",
            description: "Require non-disruptive upgrade",
        },
        vrf: {label: "VRF", dataType: "string"},
        zoneName: {label: "Zone", shown: false, dataType: "string"},
        tagIds: {label: "Tags", dataType: "array", options: optionMapTags},
        configuredInfo: {label: "Configured Info", dataType: "string"},
        configuredType: {label: "Configured Type", dataType: "string"},
        configBlock: {label: "Filtered Configuration Block", dataType: "string"},
        configuredBy: {label: "Configured By", dataType: "string"},
        configuredOn: {label: "Configured On", dataType: "string", shown: false},
        accessStatus: {
            label: "Access Status",
            shown: false,
            dataType: "enum",
            options: optionMapAccessStatus,
        },
        latestConfig: {
            dataType: "longstring",
            label: "Latest Configuration Snapshot",
            name: "latestConfig",
            options: [],
        },
        defaultConfig: {
            dataType: "longstring",
            label: "Default Configuration Snapshot",
            name: "defaultConfig",
            options: [],
        },
        stateData: {
            dataType: "longstring",
            label: "Latest Operational Snapshot",
            name: "stateData",
            options: [],
        },
    };
};

// Mirrors the id normalization in
// EditAuditPolicyComponent._appendColumnConfigWithNodeProps.
const _nodePropFieldName = (id) => `nodeProps.${String(id).replace(/[\t\n\r ]+/g, " ")}`;

/**
 * The set of field names valid in a rule's ifExpression: the base device
 * column keys (the column options need no real data — only the keys matter
 * here, so the builder is called with defaults) plus `nodeProps.<id>`. Used by
 * the policy-level JSON editor, which has no per-rule columnConfig to read.
 *
 * @param {Object} [nodeProps] - map keyed by node-prop id.
 * @returns {Array<string>}
 */
const buildExpressionFieldNames = (nodeProps) =>
    Object.keys(buildDeviceFilterColumns()).concat(
        Object.keys(nodeProps || {}).map(_nodePropFieldName)
    );

module.exports = {buildDeviceFilterColumns, buildExpressionFieldNames};
